/**
 * Cards never call APIs — every chip, row and action SAYS something back into
 * the conversation. `ConversationChat` mounts a `SayProvider` around each message, so a chip answers the member who
 * wrote that message when it was addressed to the person (`answersFor`), through the chat's own send. The simple
 * view's page-level provider covers what sits outside a message; with no provider at all a card posts the reply
 * directly.
 */
import React, { createContext, useCallback, useContext } from 'react';
import { conversationsApi } from '../../../services/api/conversations.api.js';

export type SayFn = (message: string) => void;

export const SayContext = createContext<SayFn | null>(null);

export function SayProvider({ say, children }: { say: SayFn; children: React.ReactNode }) {
  return <SayContext.Provider value={say}>{children}</SayContext.Provider>;
}

/** Raw context access — `null` when no provider is mounted. */
export function useSay(): SayFn | null {
  return useContext(SayContext);
}

/** The provider's `say` when mounted, otherwise a direct reply to `conversationId`. */
export function useConversationSay(conversationId: string): SayFn {
  const provided = useContext(SayContext);
  return useCallback(
    (message: string) => {
      if (provided) {
        provided(message);
        return;
      }
      // No provider: a card rendered outside a conversation's chat.
      void conversationsApi.reply({
        path: { id: conversationId },
        body: { role: 'user', content: message, surface: 'detailed' },
      });
    },
    [provided, conversationId],
  );
}
